import { useEffect, useState } from "react";
import { confirmAction } from "@/components/app/Confirm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function EmailTemplates() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["email-templates"], queryFn: () => get("/settings/email-templates") });
  const [key, setKey] = useState("application_received");
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const t = data?.find((x) => x.key === key);
  useEffect(() => { if (t) { setForm({ subject: t.subject, body: t.body }); setPreview(null); } }, [t]);
  if (isLoading || !form) return <Skel className="h-96" />;
  const refresh = () => qc.invalidateQueries({ queryKey: ["email-templates"] });
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const doPreview = async () => { try { setPreview((await api.post(`/settings/email-templates/${key}/preview`, form)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid gap-5 lg:grid-cols-[220px_1fr]" data-tour="pt-email-templates-page" data-testid="email-templates-page">
      <nav className="panel flex flex-col p-2">
        {data.map((x) => <button key={x.key} onClick={() => setKey(x.key)} data-testid={`template-${x.key}`}
          className={cn("rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150", key === x.key ? "bg-brand-tint font-medium text-brand" : "text-ink-secondary hover:bg-subtle")}>{x.name}{x.customised && <span className="ml-1 text-[10px] text-[#7A5300]">edited</span>}</button>)}
      </nav>
      <section className="panel space-y-3 p-5">
        <h2 className="text-lg font-semibold">{t.name}</h2>
        <p className="text-xs text-ink-muted">Placeholders: {t.vars.map((v) => `{{${v}}}`).join("  ")}</p>
        <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-tour="pt-template-subject-input" data-testid="template-subject-input" />
        <Textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="font-mono text-xs" data-testid="template-body-input" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run(() => api.put(`/settings/email-templates/${key}`, form), "Template saved")} data-tour="pt-template-save-btn" data-testid="template-save-btn">Save</Button>
          <Button variant="outline" onClick={doPreview} data-tour="pt-template-preview-btn" data-testid="template-preview-btn">Preview</Button>
          {t.customised && <Button variant="ghost" onClick={async () => (await confirmAction({ title: "Reset this template?", body: "Your custom subject and body are replaced by the default text.", entity: key, confirmLabel: "Reset to default" })) && run(() => api.delete(`/settings/email-templates/${key}`), "Reset to default")} data-testid="template-reset-btn">Reset to default</Button>}
        </div>
        {preview && <div className="space-y-1"><p className="text-sm font-medium" data-testid="template-preview-subject">{preview.subject}</p><iframe title="preview" srcDoc={preview.html} className="h-72 w-full rounded-[5px] border border-line bg-white" /></div>}
      </section>
    </div>
  );
}
